/**
 * Platform Admin Page — Bundle 3 (corrected architecture).
 *
 * INTERNAL OPERATIONS CONSOLE — not subscriber-facing.
 *
 * Access model:
 *   - No customer JWT required. This page is outside <ProtectedRoute>.
 *   - Operator navigates directly to /platform-admin.
 *   - Locked state: shows secret input form.
 *   - Operator enters PLATFORM_ADMIN_SECRET → POST /api/v1/platform-admin/session.
 *   - On success: short-lived session JWT stored in sessionStorage ('pa_token').
 *   - Unlocked state: full admin console.
 *   - All admin API calls use `paApi` which injects X-PA-Token header.
 *   - "End session" clears sessionStorage and returns to locked state.
 *
 * Design tokens match the rest of the app.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPlanLabel, normalizePlanId } from '../lib/plans';
import i18n from '../i18n/i18n';
import { formatDate, formatDateTime, formatNumber } from '../i18n/formatters';
import { usePageMeta } from '../lib/page-meta';
import BrandLogo from '../components/brand/BrandLogo';
import PlatformBroadcastsPanel from '../components/platform-admin/PlatformBroadcastsPanel';
import PartnerProgramPanel from '../components/platform-admin/PartnerProgramPanel';
import type {
  PlatformStats,
  AdminOrgSummary,
  QuotaOverride,
  PlatformAdminAuditLog,
  PlatformOperatorAuditLog,
} from '@accestra/types';

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const C = {
  page:    'var(--acc-color-page)',
  text:    'var(--acc-color-text)',
  muted:   'var(--acc-color-text-muted)',
  border:  'var(--acc-color-border)',
  accent:  'var(--acc-color-primary)',
  surface: 'var(--card-bg)',
  surfaceSecondary: 'var(--surface-secondary)',
  buttonSecondary: 'var(--button-secondary-bg)',
  inputBg: 'var(--input-bg)',
  danger:  'var(--acc-color-danger)',
  warning: 'var(--acc-color-warning)',
  success: 'var(--acc-color-success)',
};

// ---------------------------------------------------------------------------
// Session token storage (sessionStorage → cleared on tab/browser close)
// ---------------------------------------------------------------------------

const PA_TOKEN_KEY = 'pa_token';
const API_BASE     = (import.meta.env['VITE_API_BASE_URL'] ?? '') + '/api/v1';

function getToken(): string | null {
  return sessionStorage.getItem(PA_TOKEN_KEY);
}
function storeToken(token: string): void {
  sessionStorage.setItem(PA_TOKEN_KEY, token);
}
function clearToken(): void {
  sessionStorage.removeItem(PA_TOKEN_KEY);
}

// ---------------------------------------------------------------------------
// Platform-admin-specific API client
// All calls inject X-PA-Token header instead of customer Bearer token.
// ---------------------------------------------------------------------------

class PaApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'PaApiError';
  }
}

async function paFetch<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const token   = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['X-PA-Token'] = token;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  const data = await res.json();
  if (!data.success) {
    throw new PaApiError(res.status, data.error?.message ?? 'Unknown error');
  }
  return (data as { success: true; data: T }).data;
}

function paErrMsg(err: unknown, fallback = i18n.t('platformAdmin:errors.generic')): string {
  if (err instanceof PaApiError) {
    if (err.status === 401) return i18n.t('platformAdmin:errors.sessionExpired');
    return err.message || fallback;
  }
  return fallback;
}

function formatAdminDateTime(value: string | Date | null | undefined): string {
  return formatDateTime(value) || i18n.t('common:labels.notAvailable');
}

function formatAdminDate(value: string | Date | null | undefined): string {
  return formatDate(value) || i18n.t('common:labels.notAvailable');
}

type OrgNextActionId =
  | 'needsFirstScan'
  | 'invitePending'
  | 'exportNotUsed'
  | 'authAttention'
  | 'noRecentOperatorActivity'
  | 'readyForPilotReview'
  | 'healthyFollowThrough';

function billingStatusLabel(status: string): string {
  switch (status) {
    case 'active':
    case 'trialing':
    case 'past_due':
    case 'canceled':
    case 'unpaid':
      return i18n.t(`platformAdmin:billingStatus.${status}` as const);
    default:
      return i18n.t('platformAdmin:billingStatus.none');
  }
}

function authModeLabel(mode: string): string {
  switch (mode) {
    case 'bearer_token':
    case 'session_storage_state':
    case 'form_login':
      return i18n.t(`platformAdmin:authModes.${mode}` as const);
    default:
      return i18n.t('platformAdmin:authModes.none');
  }
}

function authTestStatusLabel(status: string | null): string {
  switch (status) {
    case 'passed':
    case 'failed':
    case 'invalid':
      return i18n.t(`platformAdmin:authTestStatus.${status}` as const);
    default:
      return i18n.t('platformAdmin:authTestStatus.not_tested');
  }
}

function authFailureLabel(category: string | null): string {
  switch (category) {
    case 'session_expired':
    case 'redirected_to_login':
    case 'redirected_to_login_mid_scan':
    case 'selector_missing':
    case 'auth_token_rejected':
    case 'auth_session_invalidated':
      return i18n.t(`platformAdmin:authFailure.${category}` as const);
    default:
      return i18n.t('common:labels.notAvailable');
  }
}

function scanStatusLabel(status: string | null): string {
  switch (status) {
    case 'completed':
    case 'completed_with_failures':
    case 'queued':
    case 'running':
    case 'failed':
    case 'auth_failed':
      return i18n.t(`platformAdmin:scanStatus.${status}` as const);
    default:
      return i18n.t('platformAdmin:scanStatus.noScheduledRun');
  }
}

function fleetHealthLabel(state: string | null | undefined): string {
  switch (state) {
    case 'auth_revalidation_needed':
    case 'regression_detected':
    case 'unresolved_critical_issues_present':
    case 'attention_needed':
    case 'healthy':
      return i18n.t(`platformAdmin:fleetHealth.${state}` as const);
    default:
      return i18n.t('platformAdmin:fleetHealth.unavailable');
  }
}

function regressionStateLabel(state: string | null | undefined): string {
  switch (state) {
    case 'baseline':
    case 'improved':
    case 'stable':
    case 'regressed':
      return i18n.t(`platformAdmin:regressionState.${state}` as const);
    default:
      return i18n.t('common:labels.notAvailable');
  }
}

function regressionSummaryLabel(state: string | null | undefined): string {
  switch (state) {
    case 'baseline':
    case 'improved':
    case 'stable':
    case 'regressed':
      return i18n.t(`common:regressionSummary.${state}` as const);
    default:
      return i18n.t('platformAdmin:detailCards.operationalHealth.noAdditionalRegressionSummary');
  }
}

function pilotReadinessStateLabel(state: string): string {
  switch (state) {
    case 'not_started':
    case 'pilot_ready':
    case 'setup_in_progress':
      return i18n.t(`platformAdmin:pilotReadiness.state.${state}` as const);
    default:
      return i18n.t('common:labels.notAvailable');
  }
}

function pilotReadinessSummary(org: AdminOrgSummary): string {
  if (org.pilotReadiness.state === 'not_started') {
    return i18n.t('platformAdmin:pilotReadiness.summary.notStarted');
  }

  if (org.pilotReadiness.state === 'pilot_ready') {
    return org.pendingInvitesCount > 0
      ? i18n.t('platformAdmin:pilotReadiness.summary.readyWithPendingInvites')
      : i18n.t('platformAdmin:pilotReadiness.summary.ready');
  }

  if (!org.pilotReadiness.hasSuccessfulScan) {
    return i18n.t('platformAdmin:pilotReadiness.summary.needsFirstScan');
  }

  if (!org.pilotReadiness.reviewedIssues) {
    return i18n.t('platformAdmin:pilotReadiness.summary.reviewIssues');
  }

  if (org.reportPackReady && !org.exportedReportAt) {
    return i18n.t('platformAdmin:pilotReadiness.summary.exportReport');
  }

  return i18n.t('platformAdmin:pilotReadiness.summary.setupInProgress');
}

function workflowStateLabel(state: string): string {
  switch (state) {
    case 'open':
    case 'acknowledged':
    case 'in_progress':
    case 'resolved':
    case 'false_positive':
    case 'suppressed':
      return i18n.t(`platformAdmin:workflowState.${state}` as const);
    default:
      return state.replace(/_/g, ' ');
  }
}

function membershipRoleLabel(role: 'owner' | 'admin' | 'member' | null): string {
  switch (role) {
    case 'owner':
    case 'admin':
    case 'member':
      return i18n.t(`common:roles.${role}` as const);
    default:
      return i18n.t('common:labels.notAvailable');
  }
}

function membershipRoleColor(role: 'owner' | 'admin' | 'member' | null): string {
  switch (role) {
    case 'owner':
      return C.accent;
    case 'admin':
      return C.warning;
    case 'member':
      return C.muted;
    default:
      return C.muted;
  }
}

function operatorAuditActionLabel(action: string): string {
  switch (action) {
    case 'site.schedule_updated':
      return i18n.t('platformAdmin:audit.operatorActions.site.schedule_updated');
    case 'site.auth_profile_saved':
      return i18n.t('platformAdmin:audit.operatorActions.site.auth_profile_saved');
    case 'site.auth_profile_tested':
      return i18n.t('platformAdmin:audit.operatorActions.site.auth_profile_tested');
    case 'site.auth_profile_deleted':
      return i18n.t('platformAdmin:audit.operatorActions.site.auth_profile_deleted');
    case 'issue.workflow_state_changed':
      return i18n.t('platformAdmin:audit.operatorActions.issue.workflow_state_changed');
    case 'issue.assignee_changed':
      return i18n.t('platformAdmin:audit.operatorActions.issue.assignee_changed');
    case 'issue.workflow_note_changed':
      return i18n.t('platformAdmin:audit.operatorActions.issue.workflow_note_changed');
    case 'issue.suppression_created':
      return i18n.t('platformAdmin:audit.operatorActions.issue.suppression_created');
    case 'issue.suppression_cleared':
      return i18n.t('platformAdmin:audit.operatorActions.issue.suppression_cleared');
    default:
      return action.replace(/[._]/g, ' ');
  }
}

function operatorAuditSummary(log: PlatformOperatorAuditLog): string {
  const metadata = log.metadata ?? {};
  switch (log.action) {
    case 'site.schedule_updated':
      return i18n.t('platformAdmin:audit.operatorSummary.scheduleUpdated');
    case 'site.auth_profile_tested':
      return typeof metadata['newStatus'] === 'string'
        ? i18n.t('platformAdmin:audit.operatorSummary.authTestResult', {
            status: authTestStatusLabel(String(metadata['newStatus'])),
          })
        : i18n.t('platformAdmin:audit.operatorSummary.authTestRecorded');
    case 'issue.suppression_created':
      return typeof metadata['reason'] === 'string'
        ? i18n.t('platformAdmin:audit.operatorSummary.suppressionReason', {
            reason: String(metadata['reason']),
          })
        : i18n.t('platformAdmin:audit.operatorSummary.suppressionCreated');
    case 'issue.suppression_cleared':
      return i18n.t('platformAdmin:audit.operatorSummary.suppressionCleared');
    case 'issue.workflow_state_changed':
      return typeof metadata['newStatus'] === 'string'
        ? i18n.t('platformAdmin:audit.operatorSummary.workflowMoved', {
            status: workflowStateLabel(String(metadata['newStatus'])),
          })
        : i18n.t('platformAdmin:audit.operatorSummary.workflowChanged');
    default:
      return i18n.t('platformAdmin:audit.operatorSummary.default');
  }
}

function nextActionLabel(action: OrgNextActionId): string {
  return i18n.t(`platformAdmin:nextActions.${action}` as const);
}

function nextActionColor(action: OrgNextActionId): string {
  switch (action) {
    case 'readyForPilotReview':
    case 'healthyFollowThrough':
      return C.success;
    case 'needsFirstScan':
    case 'authAttention':
      return C.danger;
    default:
      return C.warning;
  }
}

function adminAuditActionMeta(action: string): { label: string; color: string } {
  switch (action) {
    case 'quota.updated':
      return { label: i18n.t('platformAdmin:audit.adminActions.quota.updated'), color: C.accent };
    case 'quota.removed':
      return { label: i18n.t('platformAdmin:audit.adminActions.quota.removed'), color: C.muted };
    case 'org.disabled':
      return { label: i18n.t('platformAdmin:audit.adminActions.org.disabled'), color: C.danger };
    case 'org.enabled':
      return { label: i18n.t('platformAdmin:audit.adminActions.org.enabled'), color: C.success };
    case 'org.usage_reset':
      return { label: i18n.t('platformAdmin:audit.adminActions.org.usage_reset'), color: C.warning };
    case 'billing.plan_changed':
      return { label: i18n.t('platformAdmin:audit.adminActions.billing.plan_changed'), color: C.accent };
    case 'billing.trial_updated':
      return { label: i18n.t('platformAdmin:audit.adminActions.billing.trial_updated'), color: C.warning };
    case 'billing.trial_extended':
      return { label: i18n.t('platformAdmin:audit.adminActions.billing.trial_extended'), color: C.warning };
    default:
      return { label: action, color: C.muted };
  }
}

// ---------------------------------------------------------------------------
// Tiny reusable primitives (self-contained)
// ---------------------------------------------------------------------------

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 20, padding: 22, boxShadow: 'var(--acc-shadow-sm)', ...style,
    }}>
      {children}
    </div>
  );
}

function Btn({
  children, onClick, variant = 'default', disabled = false, size = 'md', type = 'button',
}: {
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  disabled?: boolean;
  size?: 'sm' | 'md';
  type?: 'button' | 'submit';
}) {
  const base: React.CSSProperties = {
    border: '1px solid transparent', borderRadius: 14, cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 700, opacity: disabled ? 0.55 : 1, transition: 'background 0.12s, color 0.12s, border-color 0.12s, transform 0.12s',
    padding: size === 'sm' ? '4px 10px' : '8px 18px',
    fontSize: size === 'sm' ? 12 : 13, lineHeight: 1.5,
  };
  const variants: Record<string, React.CSSProperties> = {
    default: { background: C.buttonSecondary, color: C.text, border: `1px solid ${C.border}` },
    primary: { background: C.accent,  color: '#fff', boxShadow: '0 16px 28px rgba(15, 39, 71, 0.16)'  },
    danger:  { background: C.danger,  color: '#fff', boxShadow: '0 16px 28px rgba(214, 69, 69, 0.16)'  },
    ghost:   { background: 'transparent', color: C.muted, border: `1px solid ${C.border}` },
  };
  return (
    <button type={type} style={{ ...base, ...variants[variant] }} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function Pill({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '4px 10px',
      borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: `${color}18`, color, whiteSpace: 'nowrap', border: `1px solid ${color}1f`,
    }}>
      {label}
    </span>
  );
}

function StatCard({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <Card style={{ flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: accent ?? C.text, lineHeight: 1 }}>
        {value}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Table styles
// ---------------------------------------------------------------------------

const thS: React.CSSProperties = {
  padding: '10px 14px', fontWeight: 600, fontSize: 11, color: C.muted,
  textTransform: 'uppercase', letterSpacing: '0.04em',
  background: C.surfaceSecondary, borderBottom: `1px solid ${C.border}`,
};
const tdS: React.CSSProperties = {
  padding: '11px 14px', borderBottom: `1px solid ${C.border}`,
  verticalAlign: 'middle', fontSize: 13,
};

// ---------------------------------------------------------------------------
// Quota modal
// ---------------------------------------------------------------------------

function QuotaModal({
  org, onClose, onSaved,
}: {
  org: AdminOrgSummary;
  onClose: () => void;
  onSaved: (o: QuotaOverride | null) => void;
}) {
  const { t } = useTranslation(['platformAdmin', 'common']);
  const ex = org.quotaOverride;
  const [maxSites,  setMaxSites]  = useState(ex?.maxSites?.toString()       ?? '');
  const [maxScans,  setMaxScans]  = useState(ex?.maxMonthlyScans?.toString() ?? '');
  const [expiresAt, setExpiresAt] = useState(
    ex?.expiresAt ? new Date(ex.expiresAt).toISOString().slice(0, 10) : '',
  );
  const [reason,    setReason]    = useState(ex?.reason ?? '');
  const [saving,    setSaving]    = useState(false);
  const [removing,  setRemoving]  = useState(false);
  const [error,     setError]     = useState('');

  const inputS: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', border: `1px solid ${C.border}`,
    borderRadius: 6, padding: '7px 10px', fontSize: 13, color: C.text,
    background: C.inputBg, outline: 'none',
  };
  const labelS: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: C.muted, display: 'block', marginBottom: 4,
  };

  const save = async () => {
    if (!reason.trim()) { setError(t('modals.quota.reasonRequired')); return; }
    setSaving(true); setError('');
    try {
      const body: Record<string, unknown> = { reason: reason.trim() };
      if (maxSites.trim())  body['maxSites']        = parseInt(maxSites, 10);
      if (maxScans.trim())  body['maxMonthlyScans'] = parseInt(maxScans, 10);
      if (expiresAt.trim()) body['expiresAt']       = new Date(expiresAt).toISOString();
      const data = await paFetch<{ override: QuotaOverride }>(
        'PATCH', `/platform-admin/organisations/${org.id}/quota`, body,
      );
      onSaved(data.override);
    } catch (err) { setError(paErrMsg(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!ex) return;
    setRemoving(true); setError('');
    try {
      await paFetch('DELETE', `/platform-admin/organisations/${org.id}/quota`);
      onSaved(null);
    } catch (err) { setError(paErrMsg(err)); }
    finally { setRemoving(false); }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: C.surface, borderRadius: 12, padding: 28, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{t('modals.quota.title')}</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{org.name}</div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:actions.close')}
            style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}
          >
            ×
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelS}>{t('modals.quota.maxSites')}</label>
              <input style={inputS} type="number" min={1} placeholder={t('modals.quota.planDefault')} value={maxSites} onChange={(e) => setMaxSites(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelS}>{t('modals.quota.maxMonthlyScans')}</label>
              <input style={inputS} type="number" min={1} placeholder={t('modals.quota.planDefault')} value={maxScans} onChange={(e) => setMaxScans(e.target.value)} />
            </div>
          </div>
          <div>
            <label style={labelS}>{t('modals.quota.expires')}</label>
            <input style={inputS} type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </div>
          <div>
            <label style={labelS}>{t('modals.quota.reason')} <span style={{ color: C.danger }}>*</span></label>
            <textarea style={{ ...inputS, minHeight: 72, resize: 'vertical', fontFamily: 'inherit' }} placeholder={t('modals.quota.reasonPlaceholder')} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          {error && <div style={{ background: 'var(--acc-badge-danger-bg)', border: '1px solid var(--acc-badge-danger-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: 'var(--acc-badge-danger-text)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 4 }}>
            <div>{ex && <Btn variant="danger" onClick={remove} disabled={removing}>{removing ? t('actions.removing') : t('actions.removeOverride')}</Btn>}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Btn>
              <Btn variant="primary" onClick={save} disabled={saving}>{saving ? t('common:actions.saving') : t('actions.saveOverride')}</Btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Org row (expandable)
// ---------------------------------------------------------------------------

function billingStatusColor(status: string): string {
  switch (status) {
    case 'active':   return C.success;
    case 'trialing': return C.warning;
    case 'past_due': return C.danger;
    case 'canceled': return C.danger;
    case 'unpaid':   return C.danger;
    default:         return C.muted;
  }
}

function authTestStatusColor(status: string | null): string {
  switch (status) {
    case 'passed': return C.success;
    case 'failed': return C.danger;
    case 'invalid': return C.warning;
    default: return C.muted;
  }
}

function scanStatusColor(status: string | null): string {
  switch (status) {
    case 'completed': return C.success;
    case 'completed_with_failures': return C.warning;
    case 'queued': return C.accent;
    case 'running': return C.warning;
    case 'failed':
    case 'auth_failed':
      return C.danger;
    default:
      return C.muted;
  }
}

function fleetHealthColor(state: string | null | undefined): string {
  switch (state) {
    case 'auth_revalidation_needed': return C.danger;
    case 'regression_detected': return C.warning;
    case 'unresolved_critical_issues_present': return C.danger;
    case 'attention_needed': return C.warning;
    case 'healthy':
    default:
      return C.success;
  }
}

function daysSince(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return null;
  return Math.floor((Date.now() - time) / (1000 * 60 * 60 * 24));
}

function getOrgNextActions(org: AdminOrgSummary): OrgNextActionId[] {
  const actions: OrgNextActionId[] = [];
  const inactiveDays = daysSince(org.operatorActivity?.lastActivityAt ?? org.lastLoginAt);

  if (!org.pilotReadiness.hasSuccessfulScan) {
    actions.push('needsFirstScan');
  }
  if (org.pendingInvitesCount > 0) {
    actions.push('invitePending');
  }
  if (org.reportPackReady && !org.exportedReportAt) {
    actions.push('exportNotUsed');
  }
  if (org.siteAuthProfiles.some((profile) => profile.reauthRequired)) {
    actions.push('authAttention');
  }
  if (inactiveDays === null || inactiveDays > 14) {
    actions.push('noRecentOperatorActivity');
  }
  if (
    org.pilotReadiness.hasSuccessfulScan &&
    org.pilotReadiness.reviewedIssues &&
    (org.reportPackReady || org.exportedReportAt)
  ) {
    actions.push('readyForPilotReview');
  }

  return actions.length > 0 ? actions : ['healthyFollowThrough'];
}

function OrgRow({
  org, onQuotaClick, onPlanClick, onTrialClick, onAction, actionLoading,
}: {
  org: AdminOrgSummary;
  onQuotaClick: (o: AdminOrgSummary) => void;
  onPlanClick:  (o: AdminOrgSummary) => void;
  onTrialClick: (o: AdminOrgSummary) => void;
  onAction: (id: string, a: 'disable' | 'enable' | 'reset-usage') => Promise<void>;
  actionLoading: string | null;
}) {
  const { t } = useTranslation(['platformAdmin', 'common']);
  const [open, setOpen] = useState(false);
  const busy = (a: string) => actionLoading === `${org.id}:${a}`;
  const nextActions = getOrgNextActions(org);
  const pilotStateLabel = pilotReadinessStateLabel(org.pilotReadiness.state);
  const pilotSummary = pilotReadinessSummary(org);
  const noValue = t('common:labels.notAvailable');
  const primaryContactLabel =
    org.activeMembersCount <= 1 || org.usersCount <= 1
      ? t('detailFields.adoption.registeredUserEmail')
      : org.primaryContactRole === 'owner'
        ? t('detailFields.adoption.primaryOwnerEmail')
        : t('detailFields.adoption.primaryWorkspaceEmail');
  const pilotStateColor =
    org.pilotReadiness.state === 'pilot_ready'
      ? C.success
      : org.pilotReadiness.state === 'setup_in_progress'
        ? C.warning
        : C.muted;

  return (
    <>
      <tr onClick={() => setOpen((v) => !v)} style={{ cursor: 'pointer', background: open ? '#f9fafb' : 'transparent' }}>
        <td style={tdS}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 4 }}>
            <div style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>{org.name}</div>
            <Pill label={pilotStateLabel} color={pilotStateColor} />
          </div>
          <div style={{ color: C.muted, fontSize: 11 }}>
            {org.slug}
            {org.pendingInvitesCount > 0 ? ` · ${t('orgRow.pendingInvites', { count: org.pendingInvitesCount })}` : ''}
          </div>
        </td>
        <td style={tdS}><Pill label={getPlanLabel(org.plan)} color={normalizePlanId(org.plan) === 'enterprise' ? C.accent : C.muted} /></td>
        <td style={tdS}>{org.isActive ? <Pill label={t('labels.active')} color={C.success} /> : <Pill label={t('labels.disabled')} color={C.danger} />}</td>
        <td style={{ ...tdS, textAlign: 'center' }}>{org.sitesCount}</td>
        <td style={{ ...tdS, textAlign: 'center' }}>{org.scansThisMonth}</td>
        <td style={{ ...tdS, textAlign: 'center' }}>
          {org.failedScansToday > 0 ? <span style={{ color: C.danger, fontWeight: 600 }}>{org.failedScansToday}</span> : <span style={{ color: C.muted }}>0</span>}
        </td>
        <td style={tdS}>{org.quotaOverride ? <Pill label={t('labels.override')} color={C.warning} /> : <span style={{ color: C.muted, fontSize: 12 }}>{noValue}</span>}</td>
        <td style={tdS}>
          <Pill
            label={billingStatusLabel(org.billingStatus ?? 'none')}
            color={billingStatusColor(org.billingStatus ?? 'none')}
          />
        </td>
        <td style={tdS}><span style={{ color: C.accent, fontSize: 12 }}>{open ? '▲' : '▼'}</span></td>
      </tr>
      {open && (
        <tr>
          <td colSpan={10} style={{ background: C.surfaceSecondary, padding: 0, borderTop: `1px solid ${C.border}` }}>
            <div style={{ padding: '18px 20px 22px', display: 'grid', gap: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ display: 'grid', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Pill label={t('labels.pilotReadiness', { state: pilotStateLabel })} color={pilotStateColor} />
                    <Pill label={org.isActive ? t('labels.workspaceActive') : t('labels.workspaceDisabled')} color={org.isActive ? C.success : C.danger} />
                    <Pill label={t('labels.billingStatus', { status: billingStatusLabel(org.billingStatus ?? 'none') })} color={billingStatusColor(org.billingStatus ?? 'none')} />
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, maxWidth: 860 }}>
                    {pilotSummary}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {nextActions.map((item) => (
                    <Pill
                      key={item}
                      label={nextActionLabel(item)}
                      color={nextActionColor(item)}
                    />
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, alignItems: 'start' }}>
              <FounderDetailCard title={t('detailCards.adoption.title')} description={t('detailCards.adoption.description')}>
                <div
                  data-testid="platform-admin-primary-contact"
                  style={{
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    background: C.surfaceSecondary,
                    padding: '10px 12px',
                    display: 'grid',
                    gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {primaryContactLabel}
                    </div>
                    {org.primaryContactRole && (
                      <Pill
                        label={membershipRoleLabel(org.primaryContactRole)}
                        color={membershipRoleColor(org.primaryContactRole)}
                      />
                    )}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, lineHeight: 1.4, wordBreak: 'break-word' }}>
                    {org.primaryContactEmail ?? noValue}
                  </div>
                </div>
                <FounderKeyValueList
                  rows={[
                    { label: t('detailFields.adoption.sites'), value: org.sitesCount },
                    { label: t('detailFields.adoption.scansMonth'), value: org.scansThisMonth },
                    { label: t('detailFields.adoption.failedToday'), value: org.failedScansToday },
                    { label: t('detailFields.adoption.alerts'), value: org.alertsCount },
                    { label: t('detailFields.adoption.members'), value: t('detailFields.adoption.membersValue', { active: org.activeMembersCount, inactive: org.inactiveMembersCount }) },
                    { label: t('detailFields.adoption.pendingInvites'), value: org.pendingInvitesCount },
                    { label: t('detailFields.adoption.latestInvite'), value: formatAdminDateTime(org.latestInviteAt) },
                    { label: t('detailFields.adoption.onboarding'), value: org.onboardingCompleted ? t('labels.completed') : t('labels.inProgress') },
                    { label: t('detailFields.adoption.lastLogin'), value: formatAdminDateTime(org.lastLoginAt) },
                    { label: t('detailFields.adoption.created'), value: formatAdminDate(org.createdAt) },
                  ]}
                />
              </FounderDetailCard>
              <FounderDetailCard title={t('detailCards.commercial.title')} description={t('detailCards.commercial.description')}>
                <div style={{ display: 'grid', gap: 12 }}>
                <div>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: C.text }}>{t('detailCards.commercial.quotaOverrideTitle')}</div>
                {org.quotaOverride ? (
                  <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
                    <tbody>
                      {([
                        [t('detailFields.commercial.maxSites'), org.quotaOverride.maxSites ?? noValue],
                        [t('detailFields.commercial.maxScansMonth'), org.quotaOverride.maxMonthlyScans ?? noValue],
                        [t('detailFields.commercial.expires'), org.quotaOverride.expiresAt ? formatAdminDate(org.quotaOverride.expiresAt) : t('labels.never')],
                        [t('detailFields.commercial.reason'), org.quotaOverride.reason],
                      ] as [string, string | number][]).map(([k, v]) => (
                        <tr key={k}><td style={{ color: C.muted, paddingRight: 16, paddingBottom: 4 }}>{k}</td><td style={{ fontWeight: 500, wordBreak: 'break-word' }}>{v}</td></tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>{t('detailCards.commercial.noActiveOverride')}</p>}
                </div>
                <div>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: C.text }}>{t('detailCards.commercial.effectiveLimitsTitle')}</div>
                {org.effectiveLimits ? (
                  <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
                    <tbody>
                      {([
                        [t('detailFields.commercial.plan'), getPlanLabel(org.plan)],
                        [t('detailFields.commercial.sitesCap'), formatNumber(org.effectiveLimits.maxSites)],
                        [t('detailFields.commercial.scansMonthCap'), formatNumber(org.effectiveLimits.maxScansPerMonth)],
                        [t('detailFields.commercial.historyDepth'), t('detailFields.commercial.historyDepthValue', { count: org.effectiveLimits.regressionHistoryDepth })],
                        [t('detailFields.commercial.scheduledScans'), org.effectiveLimits.scheduledScansEnabled ? t('labels.enabled') : t('labels.locked')],
                        [t('detailFields.commercial.alerts'), org.effectiveLimits.alertsEnabled ? t('labels.enabled') : t('labels.locked')],
                        [t('detailFields.commercial.authenticatedScans'), org.effectiveLimits.authenticatedScanningEnabled ? t('labels.enabled') : t('labels.locked')],
                        [t('detailFields.commercial.export'), org.effectiveLimits.complianceExportEnabled ? t('labels.enabled') : t('labels.locked')],
                      ] as [string, string][]).map(([k, v]) => (
                        <tr key={k}><td style={{ color: C.muted, paddingRight: 16, paddingBottom: 4 }}>{k}</td><td style={{ fontWeight: 500 }}>{v}</td></tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>{t('detailCards.commercial.effectiveLimitsUnavailable')}</p>}
                </div>
                <FounderKeyValueList
                  rows={[
                    { label: t('detailFields.commercial.billing'), value: billingStatusLabel(org.billingStatus ?? 'none') },
                    { label: t('detailFields.commercial.trialEnds'), value: formatAdminDate(org.trialEndsAt) },
                    { label: t('detailFields.commercial.periodEnd'), value: formatAdminDate(org.currentPeriodEnd) },
                    { label: t('detailFields.commercial.stripeId'), value: org.stripeCustomerId ?? noValue },
                  ]}
                />
                </div>
              </FounderDetailCard>
              <FounderDetailCard title={t('detailCards.readiness.title')} description={t('detailCards.readiness.description')}>
                <div style={{ display: 'grid', gap: 10, fontSize: 11 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: C.text }}>{t('detailCards.readiness.customerPilotReadiness')}</div>
                    <Pill label={pilotStateLabel} color={pilotStateColor} />
                  </div>
                  <div style={{ color: C.text, lineHeight: 1.5 }}>
                    {pilotSummary}
                  </div>
                  <div style={{ color: C.muted }}>
                    {t('detailCards.readiness.successfulScan')}: <strong style={{ color: C.text }}>{org.pilotReadiness.hasSuccessfulScan ? t('labels.yes') : t('labels.no')}</strong>
                    {' · '}
                    {t('detailCards.readiness.reviewedIssues')}: <strong style={{ color: C.text }}>{org.pilotReadiness.reviewedIssues ? t('labels.yes') : t('labels.no')}</strong>
                  </div>
                  <div style={{ color: C.muted }}>
                    {t('detailCards.readiness.reportReadiness')}: <strong style={{ color: C.text }}>{org.reportPackReady ? t('labels.ready') : t('labels.notReady')}</strong>
                    {' · '}
                    {t('detailCards.readiness.reportExported')}: <strong style={{ color: C.text }}>{org.pilotReadiness.exportedReport ? t('labels.yes') : t('labels.no')}</strong>
                  </div>
                  <div style={{ color: C.muted }}>
                    {t('detailCards.readiness.onboardingComplete')}: <strong style={{ color: C.text }}>{org.pilotReadiness.onboardingCompleted ? t('labels.yes') : t('labels.no')}</strong>
                    {' · '}
                    {t('detailCards.readiness.pendingInvites')}: <strong style={{ color: C.text }}>{org.pilotReadiness.pendingInvites}</strong>
                  </div>
                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, display: 'grid', gap: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: C.text }}>{t('detailCards.readiness.customerNextStep')}</div>
                  <div style={{ color: C.text, lineHeight: 1.5 }}>
                    {nextActions.map(nextActionLabel).join(' · ')}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {nextActions.map((item) => (
                      <Pill
                        key={item}
                        label={nextActionLabel(item)}
                        color={nextActionColor(item)}
                      />
                    ))}
                  </div>
                  <div style={{ color: C.muted, lineHeight: 1.5 }}>
                    {t('detailCards.readiness.founderGuidance')}
                  </div>
                  </div>
                </div>
              </FounderDetailCard>
              <FounderDetailCard title={t('detailCards.operationalHealth.title')} description={t('detailCards.operationalHealth.description')}>
                <div style={{ display: 'grid', gap: 12 }}>
                <div>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: C.text }}>{t('detailCards.operationalHealth.latestRegressionTitle')}</div>
                {org.latestRegression ? (
                  <div
                    style={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      background: C.surface,
                      padding: '10px 12px',
                      display: 'grid',
                      gap: 6,
                      fontSize: 11,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 12, color: C.text }}>{org.latestRegression.siteName}</div>
                        <div style={{ color: C.muted }}>
                          {org.latestRegression.comparedAt ? formatAdminDateTime(org.latestRegression.comparedAt) : t('labels.comparisonRecorded')}
                        </div>
                      </div>
                      <Pill
                        label={regressionStateLabel(org.latestRegression.state)}
                        color={
                          org.latestRegression.state === 'regressed'
                            ? C.danger
                            : org.latestRegression.state === 'improved'
                              ? C.success
                              : C.muted
                        }
                      />
                    </div>
                    <div style={{ color: C.text, lineHeight: 1.5 }}>
                      {regressionSummaryLabel(org.latestRegression.state)}
                    </div>
                    <div style={{ color: C.muted }}>
                      {t('detailCards.operationalHealth.scoreDelta', { value: `${org.latestRegression.scoreDelta > 0 ? '+' : ''}${org.latestRegression.scoreDelta}` })}
                      {' · '}
                      {t('detailCards.operationalHealth.issuesDelta', { value: `${org.latestRegression.issuesDelta > 0 ? '+' : ''}${org.latestRegression.issuesDelta}` })}
                      {' · '}
                      {t('detailCards.operationalHealth.criticalDelta', { value: `${org.latestRegression.criticalDelta > 0 ? '+' : ''}${org.latestRegression.criticalDelta}` })}
                    </div>
                    <div style={{ color: C.muted }}>
                      {t('detailCards.operationalHealth.reappearedPatterns', { count: org.latestRegression.reappearedIssueCount })}
                    </div>
                  </div>
                ) : <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>{t('detailCards.operationalHealth.noRegression')}</p>}
                </div>
                <div>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: C.text }}>{t('detailCards.operationalHealth.fleetHealthTitle')}</div>
                {org.fleetHealth ? (
                  <div
                    style={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      background: C.surface,
                      padding: '10px 12px',
                      display: 'grid',
                      gap: 6,
                      fontSize: 11,
                    }}
                  >
                    <div>
                      <Pill
                        label={fleetHealthLabel(org.fleetHealth.state)}
                        color={fleetHealthColor(org.fleetHealth.state)}
                      />
                    </div>
                    <div style={{ color: C.muted }}>
                      {t('detailCards.operationalHealth.reauthRequiredSites')}: <strong style={{ color: C.text }}>{org.fleetHealth.reauthRequiredSites}</strong>
                    </div>
                    <div style={{ color: C.muted }}>
                      {t('detailCards.operationalHealth.regressionSites')}: <strong style={{ color: C.text }}>{org.fleetHealth.regressionSites}</strong>
                    </div>
                    <div style={{ color: C.muted }}>
                      {t('detailCards.operationalHealth.openCriticalIssueSites')}: <strong style={{ color: C.text }}>{org.fleetHealth.sitesWithOpenCriticalIssues}</strong>
                    </div>
                    <div style={{ color: C.muted }}>
                      {t('detailCards.operationalHealth.cleanupCandidates')}: <strong style={{ color: C.text }}>{org.fleetHealth.cleanupCandidateEvidenceCount}</strong>
                    </div>
                  </div>
                ) : <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>{t('detailCards.operationalHealth.summaryUnavailable')}</p>}
                </div>
                </div>
              </FounderDetailCard>
              <FounderDetailCard title={t('detailCards.activity.title')} description={t('detailCards.activity.description')}>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: C.text }}>{t('detailCards.activity.operatorActivityTitle')}</div>
                {org.operatorActivity ? (
                  <div
                    style={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      background: C.surface,
                      padding: '10px 12px',
                      display: 'grid',
                      gap: 6,
                      fontSize: 11,
                    }}
                  >
                    <div style={{ color: C.muted }}>
                      {t('detailCards.activity.lastActivity')}: <strong style={{ color: C.text }}>{formatAdminDateTime(org.operatorActivity.lastActivityAt)}</strong>
                    </div>
                    <div style={{ color: C.muted }}>
                      {t('detailCards.activity.lastThirtyDays')}: <strong style={{ color: C.text }}>{t('labels.eventsCount', { count: org.operatorActivity.activityCountLast30Days })}</strong>
                    </div>
                    <div style={{ color: C.muted, lineHeight: 1.5 }}>
                      {t('detailCards.activity.operatorActivityHelp')}
                    </div>
                  </div>
                ) : <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>{t('detailCards.activity.noRecentActivity')}</p>}
              </FounderDetailCard>
              <FounderDetailCard title={t('detailCards.authAccess.title')} description={t('detailCards.authAccess.description')}>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: C.text }}>{t('detailCards.authAccess.authScanAccessTitle')}</div>
                {org.siteAuthProfiles.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {org.siteAuthProfiles.map((profile) => (
                      <div
                        key={profile.siteId}
                        style={{
                          border: `1px solid ${C.border}`,
                          borderRadius: 8,
                          background: C.surface,
                          padding: '10px 12px',
                          display: 'grid',
                          gap: 6,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 12, color: C.text }}>{profile.siteName}</div>
                            <div style={{ color: C.muted, fontSize: 11 }}>{authModeLabel(profile.authMode)}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Pill
                              label={profile.isEnabled ? t('labels.enabled') : t('labels.disabled')}
                              color={profile.isEnabled ? C.success : C.muted}
                            />
                            <Pill
                              label={authTestStatusLabel(profile.lastTestStatus)}
                              color={authTestStatusColor(profile.lastTestStatus)}
                            />
                          </div>
                        </div>
                        <div style={{ color: C.muted, fontSize: 11 }}>
                          {t('detailCards.authAccess.lastTested')}: {formatAdminDateTime(profile.lastTestedAt)}
                        </div>
                        <div style={{ display: 'grid', gap: 4, fontSize: 11 }}>
                          <div style={{ color: C.muted }}>
                            {t('detailCards.authAccess.latestAuthDiagnostic')}: {profile.lastScanAuthStatus ? authTestStatusLabel(profile.lastScanAuthStatus) : noValue}
                            {profile.lastScanAuthAt ? ` · ${formatAdminDateTime(profile.lastScanAuthAt)}` : ''}
                          </div>
                          <div style={{ color: profile.reauthRequired ? C.danger : C.muted }}>
                            {t('detailCards.authAccess.reauthRequired')}: <strong>{profile.reauthRequired ? t('labels.yes') : t('labels.no')}</strong>
                          </div>
                          <div style={{ color: C.muted }}>
                            {t('detailCards.authAccess.lastScheduledAuthScan')}: <span style={{ color: scanStatusColor(profile.lastScheduledScanStatus), fontWeight: 600 }}>{scanStatusLabel(profile.lastScheduledScanStatus)}</span>
                            {profile.lastScheduledScanAt ? ` · ${formatAdminDateTime(profile.lastScheduledScanAt)}` : ''}
                          </div>
                          {(profile.lastScheduledScanFailureCategory || profile.lastScanAuthFailureCategory) && (
                            <div style={{ color: C.danger }}>
                              {t('detailCards.authAccess.lastAuthFailure')}: {authFailureLabel(
                                profile.lastScheduledScanFailureCategory ?? profile.lastScanAuthFailureCategory,
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>{t('detailCards.authAccess.noProfiles')}</p>}
              </FounderDetailCard>
              <FounderDetailCard title={t('detailCards.founderActions.title')} description={t('detailCards.founderActions.description')}>
                <div style={{ display: 'grid', gap: 14 }}>
                <div>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: C.text }}>{t('detailCards.founderActions.billingTitle')}</div>
                <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
                  <tbody>
                    {([
                      { label: t('detailFields.founderActions.status'), value: billingStatusLabel(org.billingStatus ?? 'none') },
                      { label: t('detailFields.founderActions.trialEnds'), value: formatAdminDate(org.trialEndsAt) },
                      { label: t('detailFields.founderActions.periodEnd'), value: formatAdminDate(org.currentPeriodEnd) },
                      { label: t('detailFields.founderActions.stripeId'), value: org.stripeCustomerId ?? noValue, monospace: true },
                    ] as Array<{ label: string; value: string; monospace?: boolean }>).map((row) => (
                      <tr key={row.label}><td style={{ color: C.muted, paddingRight: 16, paddingBottom: 4 }}>{row.label}</td><td style={{ fontWeight: 500, fontFamily: row.monospace ? 'monospace' : 'inherit', fontSize: row.monospace ? 11 : 12 }}>{row.value}</td></tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <div>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: C.text }}>{t('detailCards.founderActions.actionsTitle')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  <Btn size="sm" variant="primary" onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); onQuotaClick(org); }}>
                    {org.quotaOverride ? t('actions.editQuotaOverride') : t('actions.addQuotaOverride')}
                  </Btn>
                  <Btn size="sm" variant="primary" onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); onPlanClick(org); }}>
                    {t('actions.changePlan')}
                  </Btn>
                  <Btn size="sm" variant="ghost" onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); onTrialClick(org); }}>
                    {t('actions.extendTrial')}
                  </Btn>
                  {org.isActive
          ? <Btn size="sm" variant="danger" disabled={busy('disable')} onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); void onAction(org.id, 'disable'); }}>{busy('disable') ? t('actions.disabling') : t('actions.disableOrganisation')}</Btn>
          : <Btn size="sm" variant="default" disabled={busy('enable')} onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); void onAction(org.id, 'enable'); }}>{busy('enable') ? t('actions.enabling') : t('actions.enableOrganisation')}</Btn>
                  }
                  <Btn size="sm" variant="ghost" disabled={busy('reset-usage')} onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); void onAction(org.id, 'reset-usage'); }}>{busy('reset-usage') ? t('actions.resetting') : t('actions.resetUsageWindow')}</Btn>
                </div>
                </div>
                </div>
              </FounderDetailCard>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Audit log row
// ---------------------------------------------------------------------------

function AuditRow({ log }: { log: PlatformAdminAuditLog }) {
  const { t } = useTranslation(['platformAdmin', 'common']);
  const [open, setOpen] = useState(false);
  const meta = adminAuditActionMeta(log.action);
  return (
    <>
      <tr onClick={() => setOpen((v) => !v)} style={{ cursor: 'pointer' }}>
        <td style={tdS}>{formatAdminDateTime(log.createdAt)}</td>
        <td style={tdS}><Pill label={meta.label} color={meta.color} /></td>
        <td style={{ ...tdS, fontFamily: 'monospace', fontSize: 11 }}>{log.organisationId ?? t('common:labels.notAvailable')}</td>
      </tr>
      {open && (log.beforeState || log.afterState) && (
        <tr>
          <td colSpan={3} style={{ background: C.surfaceSecondary, padding: '10px 20px', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {log.beforeState && <div style={{ marginBottom: 6 }}><strong>{t('audit.before')}:</strong> {JSON.stringify(log.beforeState, null, 2)}</div>}
            {log.afterState  && <div><strong>{t('audit.after')}:</strong> {JSON.stringify(log.afterState, null, 2)}</div>}
          </td>
        </tr>
      )}
    </>
  );
}

function FounderSummaryCard({
  title,
  subtitle,
  children,
  action,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Card style={{ display: 'grid', gap: 14, minHeight: 188, boxShadow: '0 14px 34px rgba(15, 23, 42, 0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 6 }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{subtitle}</div>
          )}
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}

function FounderDetailCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        background: C.surface,
        padding: '16px 18px',
        display: 'grid',
        gap: 12,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
      }}
    >
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: description ? 4 : 0 }}>{title}</div>
        {description && (
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.55 }}>{description}</div>
        )}
      </div>
      {children}
    </div>
  );
}

function FounderKeyValueList({
  rows,
}: {
  rows: Array<{ label: string; value: React.ReactNode }>;
}) {
  return (
    <div style={{ display: 'grid', gap: 8, fontSize: 12 }}>
      {rows.map((row) => (
        <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start' }}>
          <span style={{ color: C.muted }}>{row.label}</span>
          <strong style={{ color: C.text, textAlign: 'right', fontWeight: 600 }}>{row.value}</strong>
        </div>
      ))}
    </div>
  );
}

function OperatorAuditRow({ log }: { log: PlatformOperatorAuditLog }) {
  const { t } = useTranslation(['platformAdmin', 'common']);
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr onClick={() => setOpen((value) => !value)} style={{ cursor: 'pointer' }}>
        <td style={tdS}>{formatAdminDateTime(log.createdAt)}</td>
        <td style={tdS}>
          <Pill label={operatorAuditActionLabel(log.action)} color={C.accent} />
        </td>
        <td style={{ ...tdS, color: C.muted }}>{log.organisationId ?? t('common:labels.notAvailable')}</td>
        <td style={{ ...tdS, color: C.muted }}>{log.siteId ?? t('common:labels.notAvailable')}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4} style={{ background: C.surfaceSecondary, padding: '10px 20px' }}>
            <div style={{ fontSize: 12, color: C.text, marginBottom: 6 }}>
              {operatorAuditSummary(log)}
            </div>
            {(log.oldValue || log.newValue || log.metadata) && (
              <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {log.oldValue && <div style={{ marginBottom: 6 }}><strong>{t('audit.before')}:</strong> {JSON.stringify(log.oldValue, null, 2)}</div>}
                {log.newValue && <div style={{ marginBottom: 6 }}><strong>{t('audit.after')}:</strong> {JSON.stringify(log.newValue, null, 2)}</div>}
                {log.metadata && <div><strong>{t('audit.metadata')}:</strong> {JSON.stringify(log.metadata, null, 2)}</div>}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Change plan modal
// ---------------------------------------------------------------------------

const PLAN_OPTIONS = ['free', 'starter', 'growth', 'enterprise'] as const;

function ChangePlanModal({
  org, onClose, onSaved,
}: {
  org: AdminOrgSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(['platformAdmin', 'common']);
  const currentPlan = normalizePlanId(org.plan) ?? 'free';
  const [plan,    setPlan]    = useState<string>(currentPlan);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const inputS: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', border: `1px solid ${C.border}`,
    borderRadius: 6, padding: '7px 10px', fontSize: 13, color: C.text,
    background: C.inputBg, outline: 'none',
  };
  const labelS: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: C.muted, display: 'block', marginBottom: 4,
  };

  const save = async () => {
    if (plan === currentPlan) {
      setError(t('modals.changePlan.unchangedError', { plan: getPlanLabel(plan) }));
      return;
    }
    setSaving(true); setError('');
    try {
      await paFetch('PATCH', `/platform-admin/organisations/${org.id}/plan`, { plan });
      onSaved();
    } catch (err) { setError(paErrMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: C.surface, borderRadius: 12, padding: 28, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{t('modals.changePlan.title')}</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{org.name}</div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:actions.close')}
            style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}
          >
            ×
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelS}>{t('modals.changePlan.newPlan')}</label>
            <select style={inputS} value={plan} onChange={(e) => setPlan(e.target.value)}>
              {PLAN_OPTIONS.map((p) => (
                <option key={p} value={p}>{getPlanLabel(p)}{p === currentPlan ? ` (${t('labels.current')})` : ''}</option>
              ))}
            </select>
          </div>
          <div style={{ background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 12px', fontSize: 12, color: C.text, display: 'grid', gap: 6 }}>
            <div>{t('modals.changePlan.currentPlan')}: <strong>{getPlanLabel(org.plan)}</strong></div>
            <div>{t('modals.changePlan.billingStatus')}: <strong>{billingStatusLabel(org.billingStatus ?? 'none')}</strong></div>
            {org.quotaOverride && (
              <div style={{ color: C.warning }}>
                {t('modals.changePlan.activeQuotaOverrideWarning')}
              </div>
            )}
          </div>
          <div style={{ background: 'var(--acc-badge-warning-bg)', border: '1px solid var(--acc-badge-warning-border)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--acc-badge-warning-text)' }}>
            {t('modals.changePlan.immediateWarning')}
          </div>
          {error && <div style={{ background: 'var(--acc-badge-danger-bg)', border: '1px solid var(--acc-badge-danger-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: 'var(--acc-badge-danger-text)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Btn>
            <Btn variant="primary" onClick={save} disabled={saving}>{saving ? t('common:actions.saving') : t('actions.changePlan')}</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Extend trial modal
// ---------------------------------------------------------------------------

function ExtendTrialModal({
  org, onClose, onSaved,
}: {
  org: AdminOrgSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(['platformAdmin', 'common']);
  const defaultDate = (() => {
    const d = org.trialEndsAt ? new Date(org.trialEndsAt) : new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  })();

  const [trialEndsAt, setTrialEndsAt] = useState(defaultDate);
  const [saving,      setSaving]      = useState(false);
  const [clearing,    setClearing]    = useState(false);
  const [error,       setError]       = useState('');

  const inputS: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', border: `1px solid ${C.border}`,
    borderRadius: 6, padding: '7px 10px', fontSize: 13, color: C.text,
    background: C.inputBg, outline: 'none',
  };
  const labelS: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: C.muted, display: 'block', marginBottom: 4,
  };

  const save = async () => {
    if (!trialEndsAt) { setError(t('modals.trial.requiredError')); return; }
    const d = new Date(trialEndsAt);
    if (isNaN(d.getTime())) { setError(t('modals.trial.invalidError')); return; }
    if (d <= new Date()) { setError(t('modals.trial.mustBeFutureError')); return; }
    setSaving(true); setError('');
    try {
      await paFetch('PATCH', `/platform-admin/organisations/${org.id}/trial`, { trialEndsAt: d.toISOString() });
      onSaved();
    } catch (err) { setError(paErrMsg(err)); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    setClearing(true); setError('');
    try {
      await paFetch('PATCH', `/platform-admin/organisations/${org.id}/trial`, { trialEndsAt: null });
      onSaved();
    } catch (err) { setError(paErrMsg(err)); }
    finally { setClearing(false); }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: C.surface, borderRadius: 12, padding: 28, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{t('modals.trial.title')}</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{org.name}</div>
            {org.trialEndsAt && (
              <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>
                {t('modals.trial.currentTrialEnds')}: {formatAdminDate(org.trialEndsAt)}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:actions.close')}
            style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}
          >
            ×
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 12px', fontSize: 12, color: C.text, display: 'grid', gap: 6 }}>
            <div>{t('modals.trial.plan')}: <strong>{getPlanLabel(org.plan)}</strong></div>
            <div>{t('modals.trial.billingStatus')}: <strong>{billingStatusLabel(org.billingStatus ?? 'none')}</strong></div>
            <div style={{ color: C.muted }}>
              {t('modals.trial.helpText', {
                trialing: billingStatusLabel('trialing'),
                none: billingStatusLabel('none'),
              })}
            </div>
          </div>
          <div>
            <label style={labelS}>{t('modals.trial.newTrialEndDate')}</label>
            <input style={inputS} type="date" value={trialEndsAt} onChange={(e) => setTrialEndsAt(e.target.value)} />
          </div>
          {error && <div style={{ background: 'var(--acc-badge-danger-bg)', border: '1px solid var(--acc-badge-danger-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: 'var(--acc-badge-danger-text)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 4 }}>
            <div>
              {org.trialEndsAt && (
                <Btn variant="ghost" onClick={clear} disabled={clearing}>{clearing ? t('actions.clearing') : t('actions.clearTrial')}</Btn>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Btn>
              <Btn variant="primary" onClick={save} disabled={saving}>{saving ? t('common:actions.saving') : t('actions.setTrialEnd')}</Btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Locked state — secret entry form
// ---------------------------------------------------------------------------

function LockedView({ onUnlock }: { onUnlock: (token: string) => void }) {
  const { t } = useTranslation(['platformAdmin', 'common']);
  const [secret,  setSecret]  = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const start = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!secret.trim()) { setError(t('locked.secretRequired')); return; }
    setLoading(true); setError('');
    try {
      const data = await paFetch<{ token: string }>('POST', '/platform-admin/session', { secret: secret.trim() });
      storeToken(data.token);
      onUnlock(data.token);
    } catch (err) {
      setError(paErrMsg(err, t('locked.invalidSecret')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0f172a 0%, #111827 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: 24 }}>
      <div style={{ width: 420, background: '#1a1f35', borderRadius: 18, padding: 36, boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}>

        {/* Lock icon */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            <BrandLogo variant="navbar" />
          </div>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔐</div>
          <div style={{ fontWeight: 800, fontSize: 20, color: '#e2e8f0', letterSpacing: '-0.3px' }}>
            {t('locked.title')}
          </div>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            {t('locked.internalConsole')}
          </div>
        </div>

        <form onSubmit={start}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6 }}>
              {t('locked.inputLabel')}
            </label>
            <input
              type="password"
              autoComplete="off"
              autoFocus
              aria-label={t('locked.inputLabel')}
              data-testid="platform-admin-secret-input"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={t('locked.placeholder')}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: '#0f172a', border: '1px solid #334155',
                borderRadius: 8, padding: '10px 14px',
                fontSize: 14, color: '#e2e8f0', outline: 'none',
                caretColor: '#818cf8',
              }}
            />
          </div>

          {error && (
            <div style={{ background: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#fca5a5', marginBottom: 12 }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '10px 0', borderRadius: 8, border: 'none',
              background: loading ? '#4338ca88' : '#4f46e5',
              color: '#fff', fontWeight: 700, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: 10,
            }}
          >
            {loading ? t('locked.submitting') : t('locked.submit')}
          </button>

          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Link to="/dashboard" style={{ color: '#64748b', fontSize: 12, textDecoration: 'none' }}>
              {t('common:navigation.backToApp')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unlocked state — full admin console
// ---------------------------------------------------------------------------

function UnlockedView({ onEndSession }: { onEndSession: () => void }) {
  const { t } = useTranslation(['platformAdmin', 'common']);
  const [stats,         setStats]         = useState<PlatformStats | null>(null);
  const [orgs,          setOrgs]          = useState<AdminOrgSummary[]>([]);
  const [orgsTotal,     setOrgsTotal]     = useState(0);
  const [orgsPage,      setOrgsPage]      = useState(1);
  const PAGE_SIZE = 100;

  const [auditLogs,     setAuditLogs]     = useState<PlatformAdminAuditLog[]>([]);
  const [auditTotal,    setAuditTotal]    = useState(0);
  const [operatorAuditLogs, setOperatorAuditLogs] = useState<PlatformOperatorAuditLog[]>([]);
  const [operatorAuditTotal, setOperatorAuditTotal] = useState(0);

  const [statsLoading,  setStatsLoading]  = useState(true);
  const [orgsLoading,   setOrgsLoading]   = useState(true);
  const [auditLoading,  setAuditLoading]  = useState(true);
  const [operatorAuditLoading, setOperatorAuditLoading] = useState(true);

  const [globalError,   setGlobalError]   = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [feedback,      setFeedback]      = useState<{ msg: string; ok: boolean } | null>(null);
  const [quotaTarget,   setQuotaTarget]   = useState<AdminOrgSummary | null>(null);
  const [planTarget,    setPlanTarget]    = useState<AdminOrgSummary | null>(null);
  const [trialTarget,   setTrialTarget]   = useState<AdminOrgSummary | null>(null);

  const fleetSummary = useMemo(() => {
    const authAttention = orgs.filter((org) => (
      (org.fleetHealth?.reauthRequiredSites ?? 0) > 0 ||
      org.siteAuthProfiles.some((profile) => profile.reauthRequired)
    )).length;
    const noRecentActivity = orgs.filter((org) => {
      const days = daysSince(org.operatorActivity?.lastActivityAt ?? org.lastLoginAt);
      return days === null || days > 14;
    }).length;

    return {
      seatLimited: orgs.filter((org) => org.effectiveLimits && org.activeMembersCount >= org.effectiveLimits.maxTeamMembers).length,
      siteLimited: orgs.filter((org) => org.effectiveLimits && org.sitesCount >= org.effectiveLimits.maxSites).length,
      exportLocked: orgs.filter((org) => org.effectiveLimits && !org.effectiveLimits.complianceExportEnabled).length,
      trialing: orgs.filter((org) => org.billingStatus === 'trialing').length,
      failedToday: orgs.filter((org) => org.failedScansToday > 0).length,
      authAttention,
      needsFirstScan: orgs.filter((org) => !org.pilotReadiness.hasSuccessfulScan).length,
      pendingInvites: orgs.filter((org) => org.pendingInvitesCount > 0).length,
      exportNotUsed: orgs.filter((org) => org.reportPackReady && !org.exportedReportAt).length,
      readyForPilotReview: orgs.filter((org) => (
        org.pilotReadiness.hasSuccessfulScan &&
        org.pilotReadiness.reviewedIssues &&
        (org.reportPackReady || org.exportedReportAt !== null)
      )).length,
      noRecentActivity,
    };
  }, [orgs]);

  const endSession = async () => {
    try { await paFetch('DELETE', '/platform-admin/session'); } catch { /* ignore */ }
    clearToken();
    onEndSession();
  };

  const loadStats = useCallback(async () => {
    try {
      const data = await paFetch<PlatformStats>('GET', '/platform-admin/stats');
      setStats(data);
    } catch (err) {
      setGlobalError(paErrMsg(err));
    } finally { setStatsLoading(false); }
  }, []);

  const loadOrgs = useCallback(async (page: number) => {
    setOrgsLoading(true);
    try {
      const data = await paFetch<{ orgs: AdminOrgSummary[]; total: number }>(
        'GET', `/platform-admin/organisations?page=${page}&pageSize=${PAGE_SIZE}`,
      );
      setOrgs(data.orgs);
      setOrgsTotal(data.total);
    } catch (err) {
      setGlobalError(paErrMsg(err));
    } finally { setOrgsLoading(false); }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const data = await paFetch<{ logs: PlatformAdminAuditLog[]; total: number }>(
        'GET', '/platform-admin/audit-logs?pageSize=50',
      );
      setAuditLogs(data.logs);
      setAuditTotal(data.total);
    } catch { /* non-critical */ }
    finally { setAuditLoading(false); }
  }, []);

  const loadOperatorAudit = useCallback(async () => {
    try {
      const data = await paFetch<{ events: PlatformOperatorAuditLog[]; total: number }>(
        'GET', '/platform-admin/operator-audit-logs?pageSize=50',
      );
      setOperatorAuditLogs(data.events);
      setOperatorAuditTotal(data.total);
    } catch { /* non-critical */ }
    finally { setOperatorAuditLoading(false); }
  }, []);

  useEffect(() => { loadStats(); loadAudit(); loadOperatorAudit(); }, [loadStats, loadAudit, loadOperatorAudit]);
  useEffect(() => { loadOrgs(orgsPage); }, [orgsPage, loadOrgs]);

  const handleAction = async (orgId: string, action: 'disable' | 'enable' | 'reset-usage') => {
    const key = `${orgId}:${action}`;
    setActionLoading(key); setFeedback(null);
    try {
      await paFetch('POST', `/platform-admin/organisations/${orgId}/${action}`);
      const labels: Record<string, string> = {
        disable: t('feedback.orgDisabled'),
        enable: t('feedback.orgEnabled'),
        'reset-usage': t('feedback.usageReset'),
      };
      setFeedback({ msg: labels[action] ?? t('feedback.done'), ok: true });
      await Promise.all([loadStats(), loadOrgs(orgsPage), loadAudit(), loadOperatorAudit()]);
    } catch (err) {
      setFeedback({ msg: paErrMsg(err), ok: false });
    } finally { setActionLoading(null); }
  };

  const handleQuotaSaved = async (_override: QuotaOverride | null) => {
    setQuotaTarget(null);
    setFeedback({ msg: t('feedback.quotaSaved'), ok: true });
    await Promise.all([loadStats(), loadOrgs(orgsPage), loadAudit(), loadOperatorAudit()]);
  };

  const handleBillingSaved = async () => {
    setPlanTarget(null);
    setTrialTarget(null);
    setFeedback({ msg: t('feedback.billingUpdated'), ok: true });
    await Promise.all([loadOrgs(orgsPage), loadAudit(), loadOperatorAudit()]);
  };

  const handleExportPilotSummary = useCallback(() => {
    if (orgs.length === 0) {
      setFeedback({ msg: t('feedback.noPilotSummary'), ok: false });
      return;
    }

    const rows = [
      [
        t('exports.pilotSummary.organisation'),
        t('exports.pilotSummary.plan'),
        t('exports.pilotSummary.pilotReadiness'),
        t('exports.pilotSummary.currentNextStep'),
        t('exports.pilotSummary.activeMembers'),
        t('exports.pilotSummary.pendingInvites'),
        t('exports.pilotSummary.onboardingComplete'),
        t('exports.pilotSummary.successfulScan'),
        t('exports.pilotSummary.reviewedIssues'),
        t('exports.pilotSummary.reportPackReady'),
        t('exports.pilotSummary.reportExported'),
        t('exports.pilotSummary.failedScansToday'),
        t('exports.pilotSummary.lastOperatorActivity'),
      ],
      ...orgs.map((org) => [
        org.name,
        getPlanLabel(org.plan),
        pilotReadinessStateLabel(org.pilotReadiness.state),
        getOrgNextActions(org).map(nextActionLabel).join('; '),
        String(org.activeMembersCount),
        String(org.pendingInvitesCount),
        org.onboardingCompleted ? t('labels.yes') : t('labels.no'),
        org.pilotReadiness.hasSuccessfulScan ? t('labels.yes') : t('labels.no'),
        org.pilotReadiness.reviewedIssues ? t('labels.yes') : t('labels.no'),
        org.reportPackReady ? t('labels.yes') : t('labels.no'),
        org.exportedReportAt ? t('labels.yes') : t('labels.no'),
        String(org.failedScansToday),
        formatAdminDateTime(org.operatorActivity?.lastActivityAt ?? org.lastLoginAt),
      ]),
    ];

    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ablecomply-pilot-summary-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    setFeedback({ msg: t('feedback.pilotSummaryExported'), ok: true });
  }, [orgs, t]);

  const readiness = stats?.readiness ?? {
    email: {
      liveDeliveryEnabled: false,
      fromAddressConfigured: false,
      appBaseUrlConfigured: false,
      readyForProduction: false,
    },
    support: {
      founderSupportConfigured: false,
      readyForProduction: false,
    },
    billing: {
      stripeSecretConfigured: false,
      stripeWebhookConfigured: false,
      starterPriceConfigured: false,
      growthPriceConfigured: false,
      readyForProduction: false,
    },
  };
  const operations = stats?.operations ?? {
    workerHealthy: false,
    lastWorkerHeartbeatAt: null,
    queuedScans: 0,
    runningScans: 0,
    queuedScansStuck: 0,
    runningScansStale: 0,
    laggingScheduledSites: 0,
  };

  const totalPages = Math.ceil(orgsTotal / PAGE_SIZE);

  return (
    <div style={{ background: C.page, minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>

      {/* Top bar */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: 'linear-gradient(180deg, rgba(15, 39, 71, 0.98) 0%, rgba(11, 32, 56, 0.96) 100%)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 28px',
        minHeight: 74,
        boxShadow: '0 16px 34px rgba(8, 24, 45, 0.22)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <BrandLogo variant="navbar" />
          <span style={{ background: 'rgba(200, 155, 60, 0.16)', color: '#f5dfa6', border: '1px solid rgba(200, 155, 60, 0.22)', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999, letterSpacing: '0.08em' }}>{t('topBar.badge')}</span>
          <span style={{ color: '#dbe7f3', fontWeight: 700, fontSize: 15 }}>{t('topBar.title')}</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/dashboard" style={{ color: '#c4d4e7', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>{t('actions.backToApp')}</Link>
          <button
            onClick={() => void endSession()}
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: '#f8fafc', borderRadius: 999, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
          >
            {t('topBar.endSession')}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '34px 28px 40px' }}>

        <section style={{ marginBottom: 28 }}>
          <Card
            style={{
              padding: '24px 26px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
              borderColor: '#dbe4ff',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ maxWidth: 820 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                  {t('hero.eyebrow')}
                </div>
                <div style={{ fontSize: 30, fontWeight: 800, color: C.text, letterSpacing: '-0.04em', lineHeight: 1.05, marginBottom: 10 }}>
                  {t('hero.title')}
                </div>
                <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.7 }}>
                  {t('hero.description')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Pill label={t('hero.founderOnly')} color={C.accent} />
                <Pill label={t('hero.auditVisibility')} color={C.success} />
                <Pill label={t('hero.maskedSecrets')} color={C.muted} />
              </div>
            </div>
          </Card>
        </section>

        {/* Global error */}
        {globalError && (
          <div style={{ background: 'var(--acc-badge-danger-bg)', border: '1px solid var(--acc-badge-danger-border)', borderRadius: 8, padding: '10px 16px', color: 'var(--acc-badge-danger-text)', fontSize: 13, marginBottom: 20 }}>
            {globalError}
          </div>
        )}

        {/* Action feedback */}
        {feedback && (
          <div style={{ background: feedback.ok ? 'var(--acc-badge-success-bg)' : 'var(--acc-badge-danger-bg)', border: `1px solid ${feedback.ok ? 'var(--acc-badge-success-border)' : 'var(--acc-badge-danger-border)'}`, borderRadius: 8, padding: '10px 16px', color: feedback.ok ? 'var(--acc-badge-success-text)' : 'var(--acc-badge-danger-text)', fontSize: 13, marginBottom: 20, display: 'flex', justifyContent: 'space-between' }}>
            <span>{feedback.msg}</span>
            <button onClick={() => setFeedback(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.muted, fontSize: 16 }}>×</button>
          </div>
        )}

        {/* Stats */}
        <section style={{ marginBottom: 32 }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{t('sections.overviewTitle')}</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              {t('sections.overviewSubtitle')}
            </div>
          </div>
          {statsLoading
            ? <div style={{ color: C.muted, fontSize: 13 }}>{t('tables.loading')}</div>
            : stats && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <StatCard label={t('stats.totalOrgs')}       value={stats.totalOrganisations} />
                <StatCard label={t('stats.activeOrgs')}      value={stats.activeOrganisations} accent={C.success} />
                <StatCard label={t('stats.totalUsers')}      value={stats.totalUsers} />
                <StatCard label={t('stats.recentSignups7d')} value={stats.recentSignups7d} accent={stats.recentSignups7d > 0 ? C.accent : undefined} />
                <StatCard label={t('stats.totalSites')}      value={stats.totalSites} />
                <StatCard label={t('stats.totalScans')}      value={stats.totalScans} />
                <StatCard label={t('stats.scans24h')}        value={stats.scansLast24Hours} accent={stats.scansLast24Hours > 0 ? C.accent : undefined} />
                <StatCard label={t('stats.scans7d')}         value={stats.scansLast7Days} />
                <StatCard label={t('stats.scansMonth')}      value={stats.totalScansThisMonth} />
                <StatCard label={t('stats.failedToday')}     value={stats.failedScansToday} accent={stats.failedScansToday > 0 ? C.danger : undefined} />
                <StatCard label={t('stats.authFailed30d')}   value={stats.authFailedScans30Days} accent={stats.authFailedScans30Days > 0 ? C.warning : undefined} />
                <StatCard label={t('stats.scheduledActive')} value={stats.activeScheduledScans} />
                <StatCard label={t('stats.quotaOverrides')}  value={stats.quotaOverridesActive} accent={stats.quotaOverridesActive > 0 ? C.warning : undefined} />
              </div>
            )
          }
        </section>

        <section style={{ marginBottom: 32 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            <FounderSummaryCard
              title={t('summaryCards.commercial.title')}
              subtitle={t('summaryCards.commercial.subtitle')}
            >
              <div style={{ display: 'grid', gap: 6, fontSize: 12 }}>
                <div style={{ color: C.muted }}>{t('summaryCards.commercial.seatLimited')}: <strong style={{ color: C.text }}>{fleetSummary.seatLimited}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.commercial.siteLimited')}: <strong style={{ color: C.text }}>{fleetSummary.siteLimited}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.commercial.exportLocked')}: <strong style={{ color: C.text }}>{fleetSummary.exportLocked}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.commercial.trialing')}: <strong style={{ color: C.text }}>{fleetSummary.trialing}</strong></div>
              </div>
            </FounderSummaryCard>
            <FounderSummaryCard
              title={t('summaryCards.operations.title')}
              subtitle={t('summaryCards.operations.subtitle')}
            >
              <div style={{ display: 'grid', gap: 6, fontSize: 12 }}>
                <div style={{ color: C.muted }}>{t('summaryCards.operations.failedToday')}: <strong style={{ color: C.text }}>{fleetSummary.failedToday}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.operations.authAttention')}: <strong style={{ color: C.text }}>{fleetSummary.authAttention}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.operations.noRecentActivity')}: <strong style={{ color: C.text }}>{fleetSummary.noRecentActivity}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.operations.pendingInvites')}: <strong style={{ color: C.text }}>{fleetSummary.pendingInvites}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.operations.workerHeartbeat')}: <strong style={{ color: operations.workerHealthy ? C.success : C.warning }}>{operations.workerHealthy ? t('summaryCards.readiness.ready') : t('summaryCards.operations.stale')}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.operations.workerLastSeen')}: <strong style={{ color: C.text }}>{formatAdminDateTime(operations.lastWorkerHeartbeatAt)}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.operations.queuedBacklog')}: <strong style={{ color: C.text }}>{operations.queuedScans}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.operations.stuckRunning')}: <strong style={{ color: operations.runningScansStale > 0 ? C.danger : C.text }}>{operations.runningScansStale}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.operations.scheduleLag')}: <strong style={{ color: operations.laggingScheduledSites > 0 ? C.warning : C.text }}>{operations.laggingScheduledSites}</strong></div>
              </div>
            </FounderSummaryCard>
            <FounderSummaryCard
              title={t('summaryCards.pilot.title')}
              subtitle={t('summaryCards.pilot.subtitle')}
              action={<Btn size="sm" variant="ghost" onClick={handleExportPilotSummary}>{t('actions.exportPilotSummary')}</Btn>}
            >
              <div style={{ display: 'grid', gap: 6, fontSize: 12, marginBottom: 12 }}>
                <div style={{ color: C.muted }}>{t('summaryCards.pilot.needsFirstScan')}: <strong style={{ color: C.text }}>{fleetSummary.needsFirstScan}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.pilot.readyForPilotReview')}: <strong style={{ color: C.text }}>{fleetSummary.readyForPilotReview}</strong></div>
                <div style={{ color: C.muted }}>{t('summaryCards.pilot.exportNotUsed')}: <strong style={{ color: C.text }}>{fleetSummary.exportNotUsed}</strong></div>
              </div>
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
                {t('summaryCards.pilot.footer')}
              </div>
            </FounderSummaryCard>
            <FounderSummaryCard
              title={t('summaryCards.readiness.title')}
              subtitle={t('summaryCards.readiness.subtitle')}
            >
              <div style={{ display: 'grid', gap: 8, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: C.muted }}>{t('summaryCards.readiness.emailLive')}</span>
                  <Pill label={readiness.email.liveDeliveryEnabled ? t('summaryCards.readiness.ready') : t('summaryCards.readiness.notReady')} color={readiness.email.liveDeliveryEnabled ? C.success : C.warning} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: C.muted }}>{t('summaryCards.readiness.emailFrom')}</span>
                  <Pill label={readiness.email.fromAddressConfigured ? t('summaryCards.readiness.ready') : t('summaryCards.readiness.notReady')} color={readiness.email.fromAddressConfigured ? C.success : C.warning} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: C.muted }}>{t('summaryCards.readiness.appBaseUrl')}</span>
                  <Pill label={readiness.email.appBaseUrlConfigured ? t('summaryCards.readiness.ready') : t('summaryCards.readiness.notReady')} color={readiness.email.appBaseUrlConfigured ? C.success : C.warning} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: C.muted }}>{t('summaryCards.readiness.supportInbox')}</span>
                  <Pill label={readiness.support.founderSupportConfigured ? t('summaryCards.readiness.ready') : t('summaryCards.readiness.notReady')} color={readiness.support.founderSupportConfigured ? C.success : C.warning} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: C.muted }}>{t('summaryCards.readiness.stripeSecret')}</span>
                  <Pill label={readiness.billing.stripeSecretConfigured ? t('summaryCards.readiness.ready') : t('summaryCards.readiness.notReady')} color={readiness.billing.stripeSecretConfigured ? C.success : C.warning} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: C.muted }}>{t('summaryCards.readiness.stripeWebhook')}</span>
                  <Pill label={readiness.billing.stripeWebhookConfigured ? t('summaryCards.readiness.ready') : t('summaryCards.readiness.notReady')} color={readiness.billing.stripeWebhookConfigured ? C.success : C.warning} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: C.muted }}>{t('summaryCards.readiness.stripePrices')}</span>
                  <Pill label={readiness.billing.starterPriceConfigured && readiness.billing.growthPriceConfigured ? t('summaryCards.readiness.ready') : t('summaryCards.readiness.notReady')} color={readiness.billing.starterPriceConfigured && readiness.billing.growthPriceConfigured ? C.success : C.warning} />
                </div>
              </div>
            </FounderSummaryCard>
          </div>
        </section>

        <PlatformBroadcastsPanel />

        <PartnerProgramPanel />

        {/* Organizations */}
        <section style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                {t('sections.organisationsTitle', { count: orgsTotal })}
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                {t('sections.organisationsSubtitle')}
              </div>
            </div>
            <Btn
              variant="primary"
              size="sm"
              onClick={() => {
                loadOrgs(orgsPage);
                void loadStats();
                void loadAudit();
                void loadOperatorAudit();
              }}
            >
              {t('actions.refresh')}
            </Btn>
          </div>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {orgsLoading
              ? <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>{t('tables.loading')}</div>
              : (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thS}>{t('tables.organisation')}</th>
                        <th style={thS}>{t('tables.plan')}</th>
                        <th style={thS}>{t('tables.status')}</th>
                        <th style={{ ...thS, textAlign: 'center' }}>{t('tables.sites')}</th>
                        <th style={{ ...thS, textAlign: 'center' }}>{t('tables.scansMonth')}</th>
                        <th style={{ ...thS, textAlign: 'center' }}>{t('tables.failedToday')}</th>
                        <th style={thS}>{t('tables.quota')}</th>
                        <th style={thS}>{t('tables.billing')}</th>
                        <th style={thS}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgs.length === 0
                        ? <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: C.muted }}>{t('tables.emptyOrgs')}</td></tr>
                        : orgs.map((org) => (
                          <OrgRow
                            key={org.id} org={org}
                            onQuotaClick={setQuotaTarget}
                            onPlanClick={setPlanTarget}
                            onTrialClick={setTrialTarget}
                            onAction={handleAction}
                            actionLoading={actionLoading}
                          />
                        ))
                      }
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '12px 20px', borderTop: `1px solid ${C.border}` }}>
                      <Btn size="sm" variant="ghost" disabled={orgsPage <= 1} onClick={() => setOrgsPage((p) => p - 1)}>{t('actions.previous')}</Btn>
                      <span style={{ alignSelf: 'center', fontSize: 12, color: C.muted }}>{t('pagination.page', { page: orgsPage, total: totalPages })}</span>
                      <Btn size="sm" variant="ghost" disabled={orgsPage >= totalPages} onClick={() => setOrgsPage((p) => p + 1)}>{t('actions.next')}</Btn>
                    </div>
                  )}
                </>
              )
            }
          </Card>
        </section>

        {/* Audit log */}
        <section>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              {t('sections.auditTitle', {
                suffix: auditTotal > 0 ? t('sections.auditCountSuffix', { total: auditTotal }) : '',
              })}
            </div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              {t('sections.auditSubtitle')}
            </div>
          </div>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {auditLoading
              ? <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>{t('tables.loading')}</div>
              : auditLogs.length === 0
                ? <div style={{ padding: 32, textAlign: 'center', color: C.muted, fontSize: 13 }}>{t('tables.emptyAudit')}</div>
                : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thS}>{t('tables.timestamp')}</th>
                        <th style={thS}>{t('tables.action')}</th>
                        <th style={thS}>{t('tables.organisationId')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log) => <AuditRow key={log.id} log={log} />)}
                    </tbody>
                  </table>
                )
            }
          </Card>
        </section>

        <section style={{ marginTop: 32 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              {t('sections.operatorAuditTitle', {
                suffix: operatorAuditTotal > 0 ? t('sections.auditCountSuffix', { total: operatorAuditTotal }) : '',
              })}
            </div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              {t('sections.operatorAuditSubtitle')}
            </div>
          </div>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {operatorAuditLoading
              ? <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>{t('tables.loading')}</div>
              : operatorAuditLogs.length === 0
                ? <div style={{ padding: 32, textAlign: 'center', color: C.muted, fontSize: 13 }}>{t('tables.emptyOperatorAudit')}</div>
                : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thS}>{t('tables.timestamp')}</th>
                        <th style={thS}>{t('tables.action')}</th>
                        <th style={thS}>{t('tables.organisationId')}</th>
                        <th style={thS}>{t('tables.siteId')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operatorAuditLogs.map((log) => <OperatorAuditRow key={log.id} log={log} />)}
                    </tbody>
                  </table>
                )
            }
          </Card>
        </section>

        {/* Modals */}
        {quotaTarget && (
          <QuotaModal
            org={quotaTarget}
            onClose={() => setQuotaTarget(null)}
            onSaved={handleQuotaSaved}
          />
        )}
        {planTarget && (
          <ChangePlanModal
            org={planTarget}
            onClose={() => setPlanTarget(null)}
            onSaved={handleBillingSaved}
          />
        )}
        {trialTarget && (
          <ExtendTrialModal
            org={trialTarget}
            onClose={() => setTrialTarget(null)}
            onSaved={handleBillingSaved}
          />
        )}

      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export — manages locked/unlocked state
// ---------------------------------------------------------------------------

export default function PlatformAdminPage() {
  const { t } = useTranslation('platformAdmin');
  const [token, setToken] = useState<string | null>(getToken());

  usePageMeta({
    title: t('meta.title'),
    description: t('meta.description'),
    canonicalPath: '/platform-admin',
    robots: 'noindex,nofollow',
  });

  const handleUnlock = (tok: string) => {
    storeToken(tok);
    setToken(tok);
  };

  const handleEndSession = () => {
    clearToken();
    setToken(null);
  };

  if (!token) return <LockedView onUnlock={handleUnlock} />;
  return <UnlockedView onEndSession={handleEndSession} />;
}
